import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLiveAPIContext } from '../../contexts/LiveAPIContext';
import { useInterview } from '../../contexts/InterviewContext';

interface AudioSocketHandlerProps {
  isActive: boolean; // Whether recording should be active
  isMuted: boolean; // Whether audio is muted
}

// Create a worker for processing audio
const createAudioProcessorWorker = () => {
  const workerCode = `
    let sampleRate = 16000;
    let isRecording = false;
    let audioData = [];
    
    onmessage = function(e) {
      switch(e.data.command) {
        case 'init':
          sampleRate = e.data.sampleRate;
          break;
        case 'start':
          isRecording = true;
          audioData = [];
          break;
        case 'stop':
          isRecording = false;
          self.postMessage({ type: 'complete', audioData });
          audioData = [];
          break;
        case 'process':
          if (isRecording) {
            const inputData = e.data.audioData;
            
            // Simply collect the audio data
            audioData.push(...inputData);
            
            // Send chunks every 200ms for reduced bandwidth (was 100ms)
            // This reduces bandwidth by 50% while maintaining good real-time performance
            if (audioData.length >= sampleRate * 0.2) {
              self.postMessage({ type: 'data', audioData: audioData.slice(0) });
              audioData = [];
            }
          }
          break;
      }
    };
  `;
  
  const blob = new Blob([workerCode], { type: 'application/javascript' });
  return new Worker(URL.createObjectURL(blob));
};

/**
 * Component to handle audio recording and transmission via WebSocket
 */
const AudioSocketHandler: React.FC<AudioSocketHandlerProps> = ({ isActive, isMuted }) => {
  const { client, connected, interruptPlayback } = useLiveAPIContext();
  const { isGeneratingFeedback } = useInterview();
  const [isRecording, setIsRecording] = useState(false);
  const [audioActivityDetected, setAudioActivityDetected] = useState(false);
  const audioActivityDebounceRef = useRef<{
    timer: NodeJS.Timeout | null;
    activityEndTimer: NodeJS.Timeout | null;
    lastActivityTime: number;
    minActivityDuration: number;
    cooldownPeriod: number;
  }>({
    timer: null,
    activityEndTimer: null,
    lastActivityTime: 0,
    minActivityDuration: 500, // 500ms minimum activity duration
    cooldownPeriod: 1000 // 1 second cooldown for faster response
  });
  
  // Refs for audio handling
  const audioContextRef = useRef<AudioContext | null>(null);
  const microphoneStreamRef = useRef<MediaStream | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const mountedRef = useRef(true);
  
  // Set mounted state and cleanup on unmount
  useEffect(() => {
    // Set the mounted state to true when component mounts
    mountedRef.current = true;
    console.log('[AudioSocketHandler] Component mounted and initialized');
    
    return () => {
      // Set mounted state to false when component unmounts
      mountedRef.current = false;
      stopRecording();
      
      console.log('[AudioSocketHandler] Component unmounted and cleaned up');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // stopRecording is defined within the component scope

  // Main effect for starting/stopping audio recording based on active state and connection
  useEffect(() => {
    // Also stop recording when generating feedback
    if (isActive && connected && !isRecording && !isMuted && !isGeneratingFeedback) {
      startRecording();
    } else if ((!isActive || !connected || isMuted || isGeneratingFeedback) && isRecording) {
      stopRecording();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, connected, isMuted, isRecording, isGeneratingFeedback]); // Added isGeneratingFeedback dependency

  // Handle audio data received from the worker
  const handleAudioDataFromWorker = useCallback((e: MessageEvent) => {
    if (!mountedRef.current) return;
    
    if (e.data.type === 'data') {
      const audioData = new Float32Array(e.data.audioData);
      const buffer = new ArrayBuffer(audioData.length * 2);
      const view = new DataView(buffer);
      
      // Calculate audio level for activity detection
      let sum = 0;
      for (let i = 0; i < audioData.length; i++) {
        const s = Math.max(-1, Math.min(1, audioData[i]));
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        sum += Math.abs(s);
      }
      
      // Calculate average audio level
      const avgAudioLevel = sum / audioData.length;
      // Balanced threshold that detects speech but filters out quiet noise
      const isActiveAudio = avgAudioLevel > 0.01; // Detect speech while filtering background noise
      
      // Debounce audio activity detection
      if (isActiveAudio) {
        if (!audioActivityDetected) {
          console.log('[AudioSocketHandler] Audio activity detected.');
          setAudioActivityDetected(true);
          /* // DISABLING FRONTEND INTERRUPTION FOR TESTING
          if (interruptPlayback) {
            console.log('[AudioSocketHandler] Interrupting AI playback due to user activity.');
            interruptPlayback();
          }
          */
        }
        
        // Update last activity time
        audioActivityDebounceRef.current.lastActivityTime = Date.now();
        
        // Clear any existing end timer
        if (audioActivityDebounceRef.current.activityEndTimer) {
          clearTimeout(audioActivityDebounceRef.current.activityEndTimer);
          audioActivityDebounceRef.current.activityEndTimer = null;
        }
      } else if (audioActivityDetected) {
        // Only start the end timer if we don't already have one
        if (!audioActivityDebounceRef.current.activityEndTimer) {
          // Set a timer to mark audio as inactive after cooldown
          audioActivityDebounceRef.current.activityEndTimer = setTimeout(() => {
            console.log('[AudioSocketHandler] Audio activity ended');
            setAudioActivityDetected(false);
            audioActivityDebounceRef.current.activityEndTimer = null;
          }, audioActivityDebounceRef.current.cooldownPeriod);
        }
      }
      
      // Convert to base64
      let binary = '';
      const bytes = new Uint8Array(buffer);
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64Data = window.btoa(binary);
      
      // Send audio data if connected and not muted
      // Let Gemini handle the interruption logic as it does in AI Studio
      if (connected && !isMuted && mountedRef.current && client && !isGeneratingFeedback) {
        try {
          // Use format expected by the backend: { type: 'audio', data: base64Data }
          const message = {
            type: 'audio',
            data: base64Data
          };
          
          // Send using the client's WebSocket directly for reliability
          if (client.ws && client.ws.readyState === WebSocket.OPEN) {
            client.ws.send(JSON.stringify(message));
          } else {
            // Fallback to the client's method (which should use the same format)
            client.sendAudio(base64Data);
          }
        } catch (error) {
          console.error('[AudioSocketHandler] Error sending audio data:', error);
        }
      }
    }
  // Add dependencies for useCallback
  }, [audioActivityDetected, client, connected, isMuted, interruptPlayback, setAudioActivityDetected, isGeneratingFeedback]);

  // Function to start audio recording
  const startRecording = async () => {
    if (isRecording || !mountedRef.current || isGeneratingFeedback) return;
    
    try {
      console.log('[AudioSocketHandler] Starting audio recording...');
      
      // Initialize Audio Context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({
          sampleRate: 16000 // Use 16kHz as required by Gemini Live API for input
        });
      }
      
      // Get microphone access with optimized settings
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000, // Request 16kHz to match our AudioContext
          channelCount: 1 // Mono audio uses less bandwidth
        } 
      });
      microphoneStreamRef.current = stream;
      
      // Create audio source node
      const sourceNode = audioContextRef.current.createMediaStreamSource(stream);
      sourceNodeRef.current = sourceNode;
      
      // Create processor node using ScriptProcessorNode (deprecated but widely supported)
      const processorNode = audioContextRef.current.createScriptProcessor(1024, 1, 1);
      processorNodeRef.current = processorNode;
      
      // Create worker for audio processing
      const worker = createAudioProcessorWorker();
      workerRef.current = worker;
      
      // Initialize worker with sample rate
      worker.postMessage({
        command: 'init',
        sampleRate: audioContextRef.current.sampleRate
      });
      
      // Start recording
      worker.postMessage({ command: 'start' });
      
      // Set up audio processing
      processorNode.onaudioprocess = (e) => {
        if (!mountedRef.current || isGeneratingFeedback) return;
        
        const inputData = e.inputBuffer.getChannelData(0);
        
        worker.postMessage({
          command: 'process',
          audioData: Array.from(inputData)
        });
      };
      
      // Connect nodes
      sourceNode.connect(processorNode);
      processorNode.connect(audioContextRef.current.destination);
      
      // Add worker message handler
      worker.onmessage = handleAudioDataFromWorker;
      
      // Update state
      setIsRecording(true);
      
    } catch (error) {
      console.error('[AudioSocketHandler] Error starting recording:', error);
    }
  };

  // Function to stop audio recording
  const stopRecording = () => {
    if (!isRecording || !mountedRef.current) return;
    
    console.log('[AudioSocketHandler] Stopping audio recording...');
    
    // Stop worker
    if (workerRef.current) {
      workerRef.current.postMessage({ command: 'stop' });
      workerRef.current.terminate();
      workerRef.current = null;
    }
    
    // Disconnect and clean up audio nodes
    if (sourceNodeRef.current && processorNodeRef.current) {
      try {
        sourceNodeRef.current.disconnect();
        processorNodeRef.current.disconnect();
      } catch (error) {
        console.warn('[AudioSocketHandler] Error disconnecting audio nodes:', error);
      }
      
      sourceNodeRef.current = null;
      processorNodeRef.current = null;
    }
    
    // Stop microphone
    if (microphoneStreamRef.current) {
      try {
        microphoneStreamRef.current.getTracks().forEach(track => track.stop());
      } catch (error) {
        console.warn('[AudioSocketHandler] Error stopping microphone tracks:', error);
      }
      
      microphoneStreamRef.current = null;
    }
    
    setIsRecording(false);
    console.log('[AudioSocketHandler] Audio recording stopped');
  };

  // Empty render as this is a utility component
  return null;
};

export default AudioSocketHandler; 